import React, { useCallback, useState } from "react";
import * as config from "../../config";
import { formName as gtFormName } from "../gt-form-context/gt-form-config";
import { editDataFormName } from "../review-context/review-config";

import { useSnackbar } from "notistack";
import action from "../../components/UI/buttons/NotificationDismissButton";
import { setInitial, valueSet, resetState, removeState } from "./helpers";
export const FormControlerContext = React.createContext({
  fieldStates: {},
  fieldControlers: {},
  formContorlers: {},
  submitInProgress: false,
});

export function FormControlerContextProvider(props) {
  const [values, setValues] = useState({});
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [fetchingFormConfigInProgress, setFetchingFormConfigInProgress] =
    useState(false);
  const [formConfigs, setFormConfigs] = useState({});
  const { enqueueSnackbar } = useSnackbar();

  const registerFormField = useCallback(function (
    formName,
    fieldName,
    initialValue = "",
    required = true
  ) {
    const valueToSet = [initialValue, false, true];
    [setValues, setTouched, setErrors].forEach((stateUpdateFn, index) => {
      if (!required && index > 0) return;
      stateUpdateFn((state) =>
        setInitial(state, formName, fieldName, valueToSet[index])
      );
    });
  },
  []);

  const setFieldValue = useCallback(function (
    formName,
    fieldName,
    enteredValue
  ) {
    setValues((state) => {
      return valueSet(state, formName, fieldName, enteredValue);
    });
  },
  []);

  const setFieldTouched = useCallback(function (
    formName,
    fieldName,
    isTouched = true
  ) {
    setTouched((state) => {
      return valueSet(state, formName, fieldName, isTouched);
    });
  },
  []);

  const setFieldError = useCallback(function (
    formName,
    fieldName,
    error = true
  ) {
    setErrors((state) => {
      return valueSet(state, formName, fieldName, error);
    });
  },
  []);

  const resetForm = useCallback(function (formName) {
    const type = ["values", "touched", "errors"];
    [setValues, setTouched, setErrors].forEach((stateUpdateFn, index) => {
      stateUpdateFn((state) => resetState(state, formName, type[index]));
    });
  }, []);

  const removeFormState = useCallback(function (formName) {
    [setValues, setTouched, setErrors, setFormConfigs].forEach(
      (stateUpdateFn) => {
        stateUpdateFn((state) => removeState(state, formName));
      }
    );
  }, []);

  const blurHandler = useCallback(
    function (e) {
      const { formName, fieldName, error, required } = this;
      if (!required) return;
      setFieldTouched(formName, fieldName);
      e.target.value === "" && !error && setFieldError(formName, fieldName);
    },
    [setFieldTouched, setFieldError]
  );

  const changeHandler = useCallback(
    function (e, selectedValue) {
      const enteredValue = selectedValue || e?.target?.value;
      const { formName, fieldName, error } = this;

      setFieldValue(formName, fieldName, enteredValue);

      enteredValue !== "" && error && setFieldError(formName, fieldName, false);
    },
    [setFieldValue, setFieldError]
  );

  const getFormCofig = useCallback(
    async function (formName, path) {
      try {
        setFetchingFormConfigInProgress(true);

        const response = await config.axiosGu(path);

        const formConfig = await response.data;

        if (formName === gtFormName || formName === editDataFormName) {
          const divisonOptions = formConfig.divisions.map((division) => {
            return { id: division.id, name: division.name };
          });
          setFormConfigs((state) => ({
            ...state,
            [formName]: { ...formConfig, divisonOptions },
          }));
        } else {
          setFormConfigs((state) => ({
            ...state,
            [formName]: formConfig,
          }));
        }
      } catch (err) {
        const resStatus = err?.response?.status;
        if (resStatus !== 401) {
          enqueueSnackbar("Fetching form options failed!", {
            variant: "error",
            action,
          });
        }
      }
      setFetchingFormConfigInProgress(false);
    },
    [enqueueSnackbar]
  );

  function defaultFieldOptions(
    formName,
    { fieldName, label, type = "text", required = true }
  ) {
    return {
      fieldName,
      label,
      type,
      registerFormField,
      changeHandler,
      blurHandler,
      optionsLoadingInProgress: fetchingFormConfigInProgress,
      loadingText: `Loading ${label} options...`,
      error: errors?.[formName]?.[fieldName],
      touched: touched?.[formName]?.[fieldName],
      value: values?.[formName]?.[fieldName],
      required,
      formName,
      ...this,
    };
  }

  return (
    <FormControlerContext.Provider
      value={{
        fieldsState: {
          allValues: values,
          allErrors: errors,
          allTouched: touched,
          allConfigs: formConfigs,
        },
        controlers: {
          registerFormField,
          setFieldValue,
          setFieldTouched,
          setFieldError,
          getFormCofig,
          changeHandler,
          blurHandler,
          defaultFieldOptions,
          resetForm,
          removeFormState,
        },

        state: {
          setValues,
          setErrors,
          setTouched,
        },

        fetchingFormConfigInProgress,
      }}
    >
      {props.children}
    </FormControlerContext.Provider>
  );
}
